import { useState, useEffect, type FormEvent } from "react";
import { useAuth } from "@/context/AuthContext";
import { useEmulators } from "@/services/firebase";
import { TRANSLATIONS, Language } from '@/translations';
import { Loader2, Mail, Lock, LogIn, UserPlus, ArrowLeft, KeyRound, Eye, EyeOff } from "lucide-react";

// Google Icon Component
const GoogleIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
);

export default function Auth() {
    const { user, signIn, signUp, resetPassword, signInWithGoogle, error, loading, clearError } = useAuth();
    const [view, setView] = useState<'login' | 'signup' | 'forgot_password'>('login');
    const [email, setEmail] = useState("");
    const [confirmEmail, setConfirmEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resetSent, setResetSent] = useState(false);


    const [detectedLang, setDetectedLang] = useState<Language>(() => {
        const browserLang = navigator.language.split('-')[0];
        const supported: Language[] = ['en', 'pt', 'es', 'fr'];
        return supported.includes(browserLang as Language) ? (browserLang as Language) : 'en';
    });

    useEffect(() => {
        if (user && !loading && (view === 'login' || view === 'signup')) {
            window.location.href = '/';
        }
    }, [user, loading, view]);

    useEffect(() => {
        const detectLocation = () => {
            try {
                // Use TimeZone and Locale for a robust, zero-network detection
                const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const locale = navigator.language.split('-')[0];

                // Check Brazil
                if (timeZone.includes('Sao_Paulo') || timeZone.includes('Brasilia') || timeZone.includes('Recife') || timeZone.includes('Manaus')) {
                    setDetectedLang('pt');
                    return;
                }

                // Spanish speaking areas
                if (['America/Mexico_City', 'America/Buenos_Aires', 'America/Santiago', 'America/Bogota', 'America/Lima', 'America/Caracas', 'Europe/Madrid'].some(tz => timeZone.includes(tz))) {
                    setDetectedLang('es');
                    return;
                }

                // French speaking areas
                if (['Europe/Paris', 'Europe/Brussels', 'Europe/Monaco', 'America/Montreal', 'Africa/Casablanca'].some(tz => timeZone.includes(tz))) {
                    setDetectedLang('fr');
                    return;
                }

                // Fallback to browser locale
                const supported: Language[] = ['en', 'pt', 'es', 'fr'];
                if (supported.includes(locale as any)) {
                    setDetectedLang(locale as any);
                }
            } catch (err) {
                console.debug("Locale detection fallback triggered.");
            }
        };
        detectLocation();
    }, []);

    const t = TRANSLATIONS[detectedLang] || TRANSLATIONS.pt;

    const AuthGoogleButton = () => (
        <button
            type="button"
            onClick={async () => {
                setLocalError(null);
                try {
                    await signInWithGoogle();
                } catch (err: any) {
                    console.error("Google auth error", err);
                    setLocalError(err.message || "Erro ao conectar com Google");
                }
            }}
            className="flex w-full items-center justify-center gap-3 rounded-lg bg-white px-6 py-3.5 text-sm font-bold uppercase tracking-widest text-black hover:bg-gray-200 transition-all active:scale-[0.98] shadow-[0_0_20px_rgba(255,255,255,0.1)]"
        >
            <GoogleIcon />
            <span>
                {detectedLang === 'pt' ? 'Entrar com Google' : 'Continue with Google'}
            </span>
        </button>
    );

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setLocalError(null);

        if (view === 'signup' && email !== confirmEmail) {
            setLocalError(t.email_mismatch);
            return;
        }

        setIsSubmitting(true);
        try {
            clearError(); // Clear any previous context errors
            if (view === 'login') {
                await signIn(email, password);
            } else if (view === 'signup') {
                await signUp(email, password);
            } else if (view === 'forgot_password') {
                await resetPassword(email, detectedLang);
                if (useEmulators) {
                    console.info("PASSWORD RESET: As you are using emulators, the email was sent to the Emulator UI (usually http://localhost:4000/auth) rather than your actual inbox.");
                }
                setResetSent(true);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#000000]">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
        );
    }

    if (resetSent && view === 'forgot_password') {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#000000] px-4">
                <div className="w-full max-w-[440px] rounded-2xl bg-[#141414] p-10 shadow-2xl border border-white/5">
                    <a href="/" className="flex flex-col items-center hover:opacity-80 transition-opacity">
                        <div className="flex items-center gap-3 mb-2">
                            <h1 className="text-3xl font-black italic tracking-tighter text-white drop-shadow-2xl leading-none">AuteurDraft</h1>
                            <div className="flex flex-col items-start pt-1">
                                <span className="text-[8px] font-black italic text-white border border-white rounded-full px-1.5 py-0.5 leading-none mb-1 tracking-wider uppercase shadow-[0_0_10px_rgba(255,255,255,0.2)]">BETA</span>
                                <span className="text-[10px] text-gray-500 font-medium italic leading-none ml-0.5">
                                    v{import.meta.env.VITE_APP_VERSION || '1.6.3'}
                                </span>
                            </div>
                        </div>
                    </a>
                    <div className="w-12 h-1 bg-blue-600 mb-4"></div>
                    <p className="text-[10px] text-gray-400 tracking-[0.2em] uppercase opacity-70 mb-8">Professional Cinematic Screenwriting</p>

                    <h2 className="text-xl font-bold text-white mb-4">{t.check_email_title}</h2>
                    <p className="text-gray-400 text-sm mb-8 leading-relaxed">
                        {t.recovery_sent_msg}<br />
                        <span className="font-semibold text-white mt-1 inline-block">{email}</span>
                        {useEmulators && (
                            <span className="block mt-4 p-3 bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs rounded-lg animate-pulse">
                                ⚠️ {detectedLang === 'pt'
                                    ? 'Nota: No Modo Emulador, o e-mail não é enviado para o Gmail. Verifique a interface local do Firebase (Auth Emulator).'
                                    : detectedLang === 'es'
                                        ? 'Nota: En el Modo Emulador, el correo no se envía a Gmail. Verifique la interfaz local de Firebase (Auth Emulator).'
                                        : detectedLang === 'fr'
                                            ? 'Note : En mode émulateur, l\'e-mail n\'est pas envoyé à Gmail. Vérifiez l\'interface locale Firebase (Auth Emulator).'
                                            : 'Note: In Emulator Mode, the email is not sent to Gmail. Check the local Firebase interface (Auth Emulator).'}
                            </span>
                        )}
                    </p>

                    <button
                        onClick={() => {
                            setResetSent(false);
                            setView('login');
                        }}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-white px-6 py-3.5 text-sm font-bold uppercase tracking-widest text-black hover:bg-gray-200 transition-all active:scale-[0.98]"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        {t.back_to_login}
                    </button>
                </div>
            </div>
        );
    }


    return (
        <div className="flex min-h-screen items-center justify-center bg-[#000000] px-4">
            <div className="w-full max-w-[440px] rounded-2xl bg-[#141414] p-10 shadow-2xl border border-white/5">
                <div className="flex flex-col items-center">
                    {/* Logo Section */}
                    <a href="/" className="flex items-center gap-3 mb-1 hover:opacity-80 transition-opacity">
                        <h1 className="text-3xl font-black italic tracking-tighter text-white drop-shadow-2xl leading-none">AuteurDraft</h1>
                        <div className="flex flex-col items-start pt-1">
                            <span className="text-[8px] font-black italic text-white border border-white rounded-full px-1.5 py-0.5 leading-none mb-1 tracking-wider uppercase shadow-[0_0_10px_rgba(255,255,255,0.2)]">BETA</span>
                            <span className="text-[10px] text-gray-500 font-medium italic leading-none ml-0.5">
                                v{import.meta.env.VITE_APP_VERSION || '1.6.3'}
                            </span>
                        </div>
                    </a>
                    <div className="w-12 h-1 bg-blue-600 mb-4"></div>
                    <p className="text-[10px] text-gray-400 tracking-[0.2em] uppercase opacity-70 mb-6">Professional Cinematic Screenwriting</p>

                    <h2 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 mb-8">
                        {view === 'login' && t.login_now}
                        {view === 'signup' && t.signup_now}
                        {view === 'forgot_password' && t.forgot_password_question}
                    </h2>

                    {view === 'login' && (
                        <div className="w-full mb-6">
                            <AuthGoogleButton />
                            <div className="relative flex items-center justify-center my-6">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-white/10"></div>
                                </div>
                                <span className="relative bg-[#141414] px-2 text-[10px] uppercase text-gray-500 font-bold tracking-widest">
                                    {detectedLang === 'pt' ? 'ou entrar com email' : 'or continue with email'}
                                </span>
                            </div>
                        </div>
                    )}

                    <form className="w-full space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-[0.15em] text-white mb-2 block">
                                {t.email}
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full rounded-lg bg-[#0d0d0d] border border-white/10 px-12 py-3.5 text-sm text-white placeholder-gray-600 focus:border-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 transition-all"
                                    placeholder="nome@exemplo.com"
                                />
                            </div>
                        </div>

                        {view === 'signup' && (
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-[0.15em] text-white mb-2 block">
                                    {t.confirm_email}
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                    <input
                                        type="email"
                                        required
                                        value={confirmEmail}
                                        onChange={(e) => setConfirmEmail(e.target.value)}
                                        className="w-full rounded-lg bg-[#0d0d0d] border border-white/10 px-12 py-3.5 text-sm text-white placeholder-gray-600 focus:border-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 transition-all"
                                        placeholder="nome@exemplo.com"
                                    />
                                </div>
                            </div>
                        )}

                        {view !== 'forgot_password' && (
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-[0.15em] text-white mb-2 block">
                                    {t.password}
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full rounded-lg bg-[#0d0d0d] border border-white/10 px-12 py-3.5 text-sm text-white placeholder-gray-600 focus:border-white/30 focus:outline-none focus:ring-1 focus:ring-white/20 transition-all"
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors focus:outline-none"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {view === 'login' && (
                            <div className="flex justify-start">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setView('forgot_password');
                                        setLocalError(null);
                                        clearError();
                                    }}
                                    className="text-[11px] font-medium text-gray-500 hover:text-white transition-colors"
                                >
                                    {t.forgot_password_question}
                                </button>
                            </div>
                        )}

                        {(error || localError) && (
                            <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-center text-[11px] font-medium text-red-400">
                                {localError || (() => {
                                    if (!error) return null;
                                    const errorKey = error.replace('auth/', '').replace(/-/g, '_');
                                    return (t as any).auth_errors?.[errorKey] || error;
                                })()}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="group flex w-full items-center justify-center gap-2 rounded-lg bg-white px-6 py-3.5 text-sm font-bold uppercase tracking-widest text-black hover:bg-gray-200 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(255,255,255,0.1)]"
                        >
                            {isSubmitting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <>
                                    {view === 'login' && <LogIn className="h-4 w-4" />}
                                    {view === 'signup' && <UserPlus className="h-4 w-4" />}
                                    {view === 'forgot_password' && <KeyRound className="h-4 w-4" />}
                                    <span>
                                        {view === 'login' && t.login_now}
                                        {view === 'signup' && t.signup_now}
                                        {view === 'forgot_password' && t.send_link}
                                    </span>
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-10 pt-6 border-t border-white/5 w-full text-center">
                        <button
                            type="button"
                            onClick={() => {
                                setView(view === 'login' ? 'signup' : 'login');
                                setLocalError(null);
                                clearError();
                            }}
                            className="text-[11px] font-medium text-gray-500 hover:text-white transition-colors"
                        >
                            {view === 'login' ? (
                                <>{t.no_account_link} <span className="text-white font-bold ml-1">{t.signup_now}</span></>
                            ) : (
                                <>{t.already_have_account_link} <span className="text-white font-bold ml-1">{t.login_now}</span></>
                            )}
                        </button>

                        <div className="mt-6 flex justify-center gap-4">
                            <a href="/privacy-policy" className="text-[10px] text-gray-600 hover:text-gray-400 transition-colors">
                                {detectedLang === 'pt' ? 'Política de Privacidade' : 'Privacy Policy'}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
